import { DOCUMENT, isPlatformBrowser, isPlatformServer } from '@angular/common';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable, Optional, PLATFORM_ID, inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, finalize, Observable, switchMap, throwError } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { API_IHO_URL } from '../../app.module';
import { AuthService } from '@core/services/authentication/auth.service';
import { jwtDecode } from 'jwt-decode';
@Injectable()
export class TokenInterceptor implements HttpInterceptor {
 router = inject(Router);
 private isRefreshing = false;
 private accessToken: string | null = null;
 constructor(
  private authService: AuthService, // Inject AuthService
  private route: ActivatedRoute,
  @Inject(DOCUMENT) private document: Document,
  @Inject(PLATFORM_ID) private platformId: Object,
  @Optional() @Inject(API_IHO_URL) private baseUrl?: string
 ) {}

 intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
  if (isPlatformBrowser(this.platformId)) {
   this.accessToken = localStorage.getItem('AccessToken');
  }

  if (this.accessToken && this.accessToken != 'undefined') {
   const isTokenExpired = this.isTokenExpired(this.accessToken);

   if (isTokenExpired && !this.isRefreshing) {
    this.authService.logOutUser(); // Refresh the token if expired
   } else if (!isTokenExpired) {
    req = this.addToken(req, this.accessToken); // Attach the existing token if not expired
   }
  }

  return next.handle(req).pipe(
   catchError((error) => {
    if (error.status === 401 && !this.isRefreshing) {
     this.authService.logOutUser();
    }
    return throwError(error);
   })
  );
 }

 private addToken(request: HttpRequest<any>, token: string) {
  return request.clone({
   setHeaders: {
    Authorization: `Bearer ${token}`,
   },
  });
 }

 private isTokenExpired(token: string): boolean {
  const decoded: any = jwtDecode(token);
  const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
  return decoded.exp < currentTime; // Token is expired if exp is less than the current time
 }

 private handleTokenRefresh(req: HttpRequest<any>, next: HttpHandler) {
  this.isRefreshing = true;

  return this.authService.refreshToken().pipe(
   switchMap((newToken: string) => {
    this.isRefreshing = false;
    if (newToken) {
     this.accessToken = newToken;
     return next.handle(this.addToken(req, newToken)); // Retry the request with the new token
    } else {
     this.router.navigate(['/login']);
     return throwError('Token refresh failed');
    }
   }),
   catchError((error) => {
    this.isRefreshing = false;
    this.router.navigate(['/login']); // Redirect to login if refreshing fails
    return throwError(error);
   }),
   finalize(() => {
    this.isRefreshing = false;
   })
  );
 }
}
